import type { SortingFn } from '@tanstack/react-table';
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";

export const textSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId) as string;
  const b = rowB.getValue(columnId) as string;

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return a.localeCompare(b);
};

export const textSortCaseInsensitive: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = (rowA.getValue(columnId) as string)?.toLowerCase();
  const b = (rowB.getValue(columnId) as string)?.toLowerCase();

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return a.localeCompare(b);
};

export const numberSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId) as number;
  const b = rowB.getValue(columnId) as number;

  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  return a - b;
};

export const dateWithFormatSort: (format: string, timezone?: string) => SortingFn<any> = (format, timezone) => (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;


  const dateA = moment.tz(a as string, format, timezone || CommonDestinationTimezone).toDate().getTime();
  const dateB = moment.tz(b as string, format, timezone || CommonDestinationTimezone).toDate().getTime();

  return dateA - dateB;
};

export const dateSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const dateA = new Date(a as string | number | Date).getTime();
  const dateB = new Date(b as string | number | Date).getTime();

  return dateA - dateB;
};

export const booleanSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId) as boolean;
  const b = rowB.getValue(columnId) as boolean;

  if (a === b) return 0;
  if (a === true) return -1;
  return 1;
};

export const naturalSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId) as string;
  const b = rowB.getValue(columnId) as string;

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
  });

  return collator.compare(a, b);
};

export const sortingFunctions = {
  text: textSort,
  textCaseInsensitive: textSortCaseInsensitive,
  number: numberSort,
  date: dateSort,
  boolean: booleanSort,
  natural: naturalSort,
} as const;

export type SortingFunctionKey = keyof typeof sortingFunctions;

export function getSortingFunction(key: SortingFunctionKey): SortingFn<any> {
  return sortingFunctions[key];
}
